// The unified lead form: one URL per step (`/start/:step`), answers in a
// reducer persisted to sessionStorage, and the browser's history as the
// flow's history. This module owns navigation, the availability check and
// the submission; the engine, mapping and decisions it calls are pure.

import {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from "react-router-dom";
import { mutate } from "swr";

import Header from "components/Header";
import HeaderPhone from "components/HeaderPhone";
import { useUser, type ApiUser } from "context/UserContext";
import { useFirebase } from "firebase";

import type { Answers } from "./answers";
import {
  accountFrom,
  accountHasName,
  withAccountContact,
  type Account,
} from "./account";
import { LeadFlowAnalyticsProvider, useLeadFlowAnalytics } from "./analytics";
import { fetchAvailability } from "./availabilityClient";
import { callLineFor, type ContactError } from "./copy";
import { cameFromStep, verifyPathFor, stepPath } from "./destinations";
import { BACK_ENTRY_STATE, documentNavigationType, stepEntryOf } from "./entry";
import type { EndScreen } from "./events";
import {
  availabilityKey,
  availabilityQueryFor,
  backAction,
  firstUnansweredStep,
  holdsVisitor,
  isStepComplete,
  leadKindOf,
  nextStep,
  pendingOutOfArea,
  progress,
  routeFor,
  type LeadKind,
  type QuestionStepId,
} from "./flow";
import {
  clearStoredState,
  leadFlowReducer,
  loadState,
  resultAwaitingConfirmation,
  saveState,
  type LeadFlowAction,
  type LeadFlowState,
} from "./state";
import ContactStep from "./steps/ContactStep";
import {
  PreplanStep,
  ReadyStep,
  RelationStep,
  WhereStep,
  WhoPassedStep,
} from "./steps/DeathSteps";
import DoneStep from "./steps/DoneStep";
import OtherOptionsStep from "./steps/OtherOptionsStep";
import OutOfAreaStep from "./steps/OutOfAreaStep";
import {
  LovedDetailsStep,
  LovedHealthStep,
  LovedRelationStep,
  LovedStep,
  PlanningForStep,
  YouHealthStep,
  YouStep,
} from "./steps/PlanningSteps";
import StartStep from "./steps/StartStep";
import type { StepProps } from "./steps/types";
import {
  decideAfterSubmit,
  failureOf,
  toSubmitBody,
  type LeadResult,
  type SubmitAnswer,
  type SubmitDecision,
} from "./submit";
import { newIdempotencyKey, postSubmission } from "./submitClient";

/** The beat between a choice and the next screen, long enough to see the
 * choice register. */
const AUTO_ADVANCE_MS = 220;
const IN_PROGRESS_RETRIES = 3;
const IN_PROGRESS_RETRY_MS = 2000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** The signed-in visitor's account, or null for an anonymous one. */
function useAccount(
  signedIn: boolean,
  profile: ApiUser | null,
): Account | null {
  return useMemo(
    () => (signedIn ? accountFrom(profile) : null),
    [signedIn, profile],
  );
}

function FlowShell({
  kind,
  children,
}: {
  kind: LeadKind | null;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-[#FBF7EC]">
      <Header
        quoteFlow
        quoteFlowWidth="form"
        actions={kind && <HeaderPhone line={callLineFor(kind)} />}
      />
      {children}
    </div>
  );
}

function StepContent({
  step,
  props,
  submitUi,
  onPhoneEdited,
}: {
  step: QuestionStepId;
  props: StepProps;
  submitUi: {
    submitting: boolean;
    error: ContactError | null;
    phoneRejected: boolean;
  };
  onPhoneEdited: () => void;
}) {
  switch (step) {
    case "start":
      return <StartStep {...props} />;
    case "who-passed":
      return <WhoPassedStep {...props} />;
    case "where":
      return <WhereStep {...props} />;
    case "relation":
      return <RelationStep {...props} />;
    case "ready":
      return <ReadyStep {...props} />;
    case "preplan":
      return <PreplanStep {...props} />;
    case "planning-for":
      return <PlanningForStep {...props} />;
    case "you":
      return <YouStep {...props} />;
    case "you-health":
      return <YouHealthStep {...props} />;
    case "loved":
      return <LovedStep {...props} />;
    case "loved-health":
      return <LovedHealthStep {...props} />;
    case "loved-details":
      return <LovedDetailsStep {...props} />;
    case "loved-relation":
      return <LovedRelationStep {...props} />;
    case "contact":
      return (
        <ContactStep
          {...props}
          submit={submitUi}
          onPhoneEdited={onPhoneEdited}
        />
      );
  }
}

/** Whether this page load is the first the flow has been mounted in. The
 * initial history entry is the only one with the default key. */
let flowMountedInDocument = false;

function LeadFlow({
  state,
  dispatch,
}: {
  state: LeadFlowState;
  dispatch: (action: LeadFlowAction) => void;
}) {
  const { step: slug } = useParams<{ step?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const analytics = useLeadFlowAnalytics();
  const { user: firebaseUser, logout } = useFirebase();
  const { user: profile } = useUser();
  const { answers } = state;
  const account = useAccount(firebaseUser !== null, profile);

  const stateRef = useRef(state);
  stateRef.current = state;
  const busyRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const startedRef = useRef(false);
  const documentLoadRef = useRef(
    !flowMountedInDocument && location.key === "default",
  );
  const viewedRef = useRef<string | null>(null);
  const endViewedRef = useRef<string | null>(null);

  const [checkingStep, setCheckingStep] = useState<QuestionStepId | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<ContactError | null>(null);
  const [phoneRejected, setPhoneRejected] = useState(false);

  const route = routeFor(slug, answers, state.availability);
  const landing =
    route.kind === "step"
      ? route
      : { step: firstUnansweredStep(answers), replace: true };
  const isQuestionRoute =
    slug !== "done" && slug !== "other-options" && slug !== "out-of-area";
  const onContact = isQuestionRoute && landing.step === "contact";
  const stepRef = useRef<QuestionStepId | null>(null);
  stepRef.current = isQuestionRoute && !landing.replace ? landing.step : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    flowMountedInDocument = true;
  }, []);

  useEffect(() => {
    if (!isQuestionRoute || landing.replace) return;
    const viewKey = `${location.key}:${landing.step}`;
    if (viewedRef.current === viewKey) return;
    viewedRef.current = viewKey;
    const entry = stepEntryOf({
      documentLoad: documentLoadRef.current,
      documentNavigationType: documentNavigationType(),
      navigationType,
      state: location.state,
    });
    documentLoadRef.current = false;
    analytics.stepViewed({
      step: landing.step,
      entry,
      ...progress(stateRef.current.answers, landing.step),
    });
    if (landing.step === "start" && !startedRef.current) {
      startedRef.current = true;
      analytics.started();
    }
    // Reported once per screen the visitor reaches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landing.step, landing.replace, isQuestionRoute]);

  const endScreen = endScreenOn(slug, state);
  useEffect(() => {
    if (endScreen === null) return;
    const viewKey = `${location.key}:${endScreen}`;
    if (endViewedRef.current === viewKey) return;
    endViewedRef.current = viewKey;
    analytics.endViewed(endScreen);
    // Reported once per time an end screen is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endScreen]);

  const accountSignature = account ? JSON.stringify(account) : "";
  useEffect(() => {
    if (!account) return;
    dispatch({
      type: "answer",
      update: (current) => withAccountContact(current, account),
    });
    // The account's values, not its identity, decide whether to write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountSignature]);

  useEffect(() => {
    if (onContact && state.idempotencyKey === null) {
      dispatch({ type: "ensure_key", key: newIdempotencyKey() });
    }
  }, [onContact, state.idempotencyKey, dispatch]);

  const signOut = () => {
    void logout().then(() => dispatch({ type: "reset" }));
  };

  const goTo = (path: string, from: QuestionStepId, replace = false) =>
    navigate(path, { state: { fromStep: from }, replace });

  async function submit() {
    const current = stateRef.current;
    const body = toSubmitBody(current.answers, {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      submitterName:
        account && accountHasName(account)
          ? { firstName: account.firstName, lastName: account.lastName }
          : undefined,
    });
    if (!body || busyRef.current) return;
    busyRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    setPhoneRejected(false);
    const key = current.idempotencyKey ?? newIdempotencyKey();
    dispatch({ type: "ensure_key", key });
    try {
      let answer: SubmitAnswer;
      let attempt = 0;
      do {
        if (attempt > 0) await sleep(IN_PROGRESS_RETRY_MS);
        const idToken =
          (await firebaseUser?.getIdToken().catch(() => null)) ?? null;
        answer = await postSubmission(body, key, idToken);
        attempt += 1;
      } while (
        decideAfterSubmit(answer, current.answers, firebaseUser !== null)
          .kind === "retry_same_key" &&
        attempt <= IN_PROGRESS_RETRIES
      );
      const decision = decideAfterSubmit(
        answer,
        current.answers,
        firebaseUser !== null,
      );
      analytics.submitted(decision.outcome, failureOf(decision));
      // A signed-in submission can fill the account's empty phone and adds
      // to its home, so both are read again.
      if (firebaseUser !== null && decision.outcome === "completed") {
        void mutate("/me");
        void mutate("/me/home");
      }
      applyDecision(decision, current.answers);
    } finally {
      busyRef.current = false;
      setSubmitting(false);
    }
  }

  /** Stores the result before the visitor navigates away: the flow may
   * unmount before an effect would save it. */
  function recordSubmitted(result: LeadResult) {
    const action: LeadFlowAction = { type: "submitted", result };
    saveState(leadFlowReducer(stateRef.current, action));
    dispatch(action);
  }

  function applyDecision(decision: SubmitDecision, submitted: typeof answers) {
    switch (decision.kind) {
      case "go":
        navigate(decision.to);
        // Forget the answers on this device without touching the in-memory
        // flow: emptying it here would trip the no-answers guard, which
        // redirects to /start and wins over the navigation above.
        if (decision.outcome === "completed") clearStoredState();
        return;
      case "done":
        recordSubmitted(decision.result);
        navigate(decision.to);
        return;
      case "verify":
        recordSubmitted(decision.result);
        navigate(decision.to, {
          state: {
            loginGrant: decision.grant,
            leadKind: leadKindOf(submitted),
          },
        });
        return;
      case "field_error":
        setPhoneRejected(true);
        return;
      case "retry_same_key":
        setSubmitError("unavailable");
        return;
      case "error":
        if (decision.rotateKey) dispatch({ type: "rotate_key" });
        setSubmitError(decision.reason);
        return;
    }
  }

  async function advanceFrom(step: QuestionStepId, chosen?: Answers) {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const current = stateRef.current;
    const a = chosen ?? current.answers;
    if (busyRef.current || stepRef.current !== step) return;
    if (!isStepComplete(a, step)) return;
    analytics.stepCompleted(step, a);
    if (step === "contact") {
      await submit();
      return;
    }
    const query = availabilityQueryFor(a, step);
    if (query) {
      const key = availabilityKey(query);
      let status = current.availability[key];
      if (status === undefined) {
        busyRef.current = true;
        setCheckingStep(step);
        const result = await fetchAvailability(query);
        busyRef.current = false;
        setCheckingStep(null);
        dispatch({ type: "availability", key, status: result.status });
        status = result.status;
        if (stepRef.current !== step) return;
      }
      if (holdsVisitor(query, status, stateRef.current.outOfAreaAcknowledged)) {
        analytics.outOfArea(a);
        goTo(stepPath("out-of-area"), step);
        return;
      }
    }
    const next = nextStep(a, step);
    if (next !== "submit") goTo(stepPath(next), step);
  }

  const advanceWith = (
    step: QuestionStepId,
    change: (answers: Answers) => Answers,
  ) => {
    const chosen = change(stateRef.current.answers);
    dispatch({ type: "answer", update: change });
    void advanceFrom(step, chosen);
  };

  const advanceSoon = (step: QuestionStepId) => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void advanceFrom(step);
    }, AUTO_ADVANCE_MS);
  };

  function backFor(step: QuestionStepId): (() => void) | null {
    const action = backAction(answers, step, cameFromStep(location.state));
    if (action === null) return null;
    return () => {
      if (action.kind === "history") navigate(-1);
      else {
        navigate(stepPath(action.step), {
          replace: true,
          state: BACK_ENTRY_STATE,
        });
      }
    };
  }

  const kind = leadKindOnScreen(state, slug);

  function renderBody(): ReactNode {
    if (slug === "other-options") return <OtherOptionsStep />;

    if (slug === "done") {
      if (state.result === null) {
        return <Navigate to={stepPath(firstUnansweredStep(answers))} replace />;
      }
      const { result } = state;
      return (
        <DoneStep
          result={result}
          onResult={(updated) =>
            dispatch({ type: "status_read", result: updated })
          }
          onConfirm={(method) => {
            const { verification, submissionId } = result;
            if (!verification || !submissionId) return;
            navigate(verifyPathFor(submissionId), {
              state: {
                loginGrant: verification.grant,
                leadKind: result.leadKind,
                method,
              },
            });
          }}
          onStartAgain={() => {
            clearStoredState();
            dispatch({ type: "reset" });
            navigate(stepPath("start"), { replace: true });
          }}
        />
      );
    }

    if (slug === "out-of-area") {
      const pending = pendingOutOfArea(
        answers,
        state.availability,
        state.outOfAreaAcknowledged,
      );
      if (pending === null) {
        return <Navigate to={stepPath(firstUnansweredStep(answers))} replace />;
      }
      const cameFrom = cameFromStep(location.state);
      return (
        <OutOfAreaStep
          segment={pending.query.segment}
          onBack={() => {
            if (cameFrom === pending.step) navigate(-1);
            else {
              navigate(stepPath(pending.step), {
                replace: true,
                state: BACK_ENTRY_STATE,
              });
            }
          }}
          onContinue={() => {
            dispatch({
              type: "acknowledge_out_of_area",
              key: availabilityKey(pending.query),
            });
            const next = nextStep(answers, pending.step);
            const target = next === "submit" ? "contact" : next;
            goTo(stepPath(target), pending.step, true);
          }}
        />
      );
    }

    if (route.kind === "out-of-area") {
      return <Navigate to={stepPath("out-of-area")} replace />;
    }
    if (landing.replace) {
      return <Navigate to={stepPath(landing.step)} replace />;
    }
    const step = landing.step;
    const props: StepProps = {
      answers,
      update: (change) => dispatch({ type: "answer", update: change }),
      advance: () => {
        if (!isStepComplete(stateRef.current.answers, step)) {
          analytics.stepInvalid(step);
          return;
        }
        void advanceFrom(step);
      },
      advanceSoon: () => advanceSoon(step),
      advanceWith: (change) => advanceWith(step, change),
      account,
      onSignOut: signOut,
      onBack: backFor(step),
      checking: checkingStep === step,
      pickUp: resultAwaitingConfirmation(state),
    };
    return (
      <StepContent
        step={step}
        props={props}
        submitUi={{ submitting, error: submitError, phoneRejected }}
        onPhoneEdited={() => setPhoneRejected(false)}
      />
    );
  }

  return <FlowShell kind={kind}>{renderBody()}</FlowShell>;
}

/** The kind of lead the screen is for. The landing's own result decides
 * it, since a reload leaves no answers to read it from. */
function leadKindOnScreen(
  state: LeadFlowState,
  slug: string | undefined,
): LeadKind | null {
  if (slug === "done" && state.result !== null) return state.result.leadKind;
  return state.answers.situation === null ? null : leadKindOf(state.answers);
}

/** The end screen the route is showing, or null while the route is a
 * question or is about to redirect to one. */
function endScreenOn(
  slug: string | undefined,
  state: LeadFlowState,
): EndScreen | null {
  if (slug === "other-options") return "other_options";
  if (slug === "done") return state.result === null ? null : "done";
  if (slug === "out-of-area") {
    return pendingOutOfArea(
      state.answers,
      state.availability,
      state.outOfAreaAcknowledged,
    ) === null
      ? null
      : "out_of_area";
  }
  return null;
}

export default function StartLead() {
  const { step: slug } = useParams<{ step?: string }>();
  const [state, dispatch] = useReducer(leadFlowReducer, undefined, loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const kind = leadKindOnScreen(state, slug);

  return (
    <LeadFlowAnalyticsProvider leadKind={kind}>
      <LeadFlow state={state} dispatch={dispatch} />
    </LeadFlowAnalyticsProvider>
  );
}
